import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

export type PagedResult<TItem> = {
  data: TItem[];
  totalCount: number;
};

export function useInfiniteScrolling<TItem, TError, TModel>(
  query: UseInfiniteQueryResult<InfiniteData<PagedResult<TItem>>, TError>,
  pageParser: (a: PagedResult<TItem>) => TModel[],
) {
  const { fetchNextPage, isError, data } = query;
  const { ref, inView } = useInView();
  const flatData = useFlattenedPageData(data, pageParser);

  // Virtualized table may not render new last row in the table after loading a new page
  // and for some reason inView is TRUE in this situation, so in order to determine
  // whether last element is actually rendered we introduce our own ref
  const trackingRef = useRef<Element | null>(null);

  useEffect(() => {
    if (isError) return;
    if (
      trackingRef.current &&
      inView &&
      data &&
      flatData &&
      flatData.length < data.pages[0].totalCount! &&
      !query.isFetchingNextPage &&
      !query.isFetching
    ) {
      void fetchNextPage();
    }
  }, [
    inView,
    flatData,
    query.isFetchingNextPage,
    query.isFetching,
    trackingRef.current,
  ]);

  return {
    ref: (el: Element | null) => {
      trackingRef.current = el;
      ref(el);
    },
    flatData,
  };
}

const useFlattenedPageData = <TData, TModel>(
  rawData: InfiniteData<TData> | undefined,
  pageParser: (a: TData) => TModel[],
): TModel[] =>
  useMemo(
    () => rawData?.pages.map(pageParser).flatMap((page) => page) ?? [],
    [rawData],
  );
